"use client";

import "video.js/dist/video-js.css";

import { AnimatePresence, motion } from "framer-motion";
import { PlayNextButton, PlayPreviousButton } from "@/components/SkipButtons";
import React, { useEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import BigPlayButton from "@/components/BigPlayButton";
import { BsChevronRight } from "react-icons/bs";
import { Button } from "@/components/ui/button";
import { COLORS } from "../../styles/colors";
import { Icons } from "../../icons/icons";
import Image from "next/image";
import { ImVolumeMute2 } from "react-icons/im";
import { MdEmail } from "react-icons/md";
import PlayerOverlays from "../shared/PlayerOverlays";
import { PortalFooter } from "./PortalSubComponents";
import { ProjectInfo } from "../../../APITypes/ProjectInfo.cs";
import { ProjectStreamData } from "../../../APITypes/ProjectStreamData.cs";
import type {
  PublicGatedPreview,
  PublicWatermark,
} from "@/lib/public-investigation";
import TrackEvent from "@/lib/trackEvent";
import { VmPlayer } from "../shared/VmPlayer";
import { cn } from "@/lib/utils";
import { useDataContext } from "@/app/_context";
import { useMeasure } from "react-use";
import { useSearchParams } from "next/navigation";
import { useSwipeable } from "react-swipeable";

export default function Portal(props: {
  pid: string;
  video: number;
  data: ProjectStreamData;
  compact: boolean;
  meta: ProjectInfo;
  /** Player overlays from the desktop app's investigation settings. */
  watermark?: PublicWatermark | null;
  gatedPreview?: PublicGatedPreview | null;
  /** Where each highlight clip starts in its session's recording, by id. */
  recordingOffsets?: Record<string, number>;
  /** True when ?story= narrowed the reel to part of the project. */
  narrowed?: boolean;
}) {
  const {
    currentSessionIndex: currentIndex,
    setVideoJsOptions,
    setIsAudioMuted,
    playerInstance: player,
    isPaused: isPlayerPaused,
    isAudioMuted: isMuted,
    currentSessionIndex,
    setCurrentSessionIndex,
    setCurrentPlaybackTime,
    showBusinessCard: portalShowBusinessCard,
    isHighlightsOnlyMode,
    setIsPortal,
    currentPlaylist,
    setCurrentPlaylist,
    wasBusinessCardShown,
    isBusinessCardPlaying,
    setCurrentProjectInfo,
    setVideoCanPlay,
  } = useDataContext();

  const [playerWeb, { width: playerWebWidth }] = useMeasure<HTMLDivElement>();
  const highlightVideos = props.data.highlightVideos ?? [];
  // The widget is a highlights reel, but a project with no highlights at all
  // should still play — fall back to the full session playlist instead of
  // dropping through to the "Project Not Found" branch.
  const playlist =
    highlightVideos.length > 0
      ? highlightVideos
      : (props.data.sessionPlaylist ?? []);
  const visiblePlaylist = playlist.length > 0 ? playlist : currentPlaylist;
  const videoDiv = useRef<HTMLDivElement>(null);
  const [virginState, setVirginState] = useState(true);
  const searchParams = useSearchParams();
  const isDevParam = searchParams.get("dev") === "true";
  const [isPipParam, setIsPipParam] = useState(false);
  const [isLoadingOpen, setIsLoadingOpen] = useState(false);
  const currentItemId = visiblePlaylist[currentIndex]?.id;
  const overlay =
    props.watermark || props.gatedPreview ? (
      <PlayerOverlays
        watermark={props.watermark ?? null}
        gatedPreview={props.gatedPreview ?? null}
        recordingTimeMs={(playerMs) =>
          (props.recordingOffsets?.[currentItemId ?? ""] ?? 0) + playerMs
        }
      />
    ) : undefined;

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === "stone:pip") setIsPipParam(!!e.data.active);
      if (e.data?.type === "stone:open") {
        setVirginState(false);
        setIsLoadingOpen(true);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // clear spinner once player signals it's ready
  useEffect(() => {
    if (!isLoadingOpen) return;
    const t = setTimeout(() => setIsLoadingOpen(false), 3000);
    return () => clearTimeout(t);
  }, [isLoadingOpen]);

  const hasContent = Boolean(
    visiblePlaylist.length > 0 && visiblePlaylist[currentIndex]?.poster,
  );

  useEffect(() => {
    setCurrentProjectInfo(props.meta);
  }, [props.meta]);

  useEffect(() => {
    setCurrentPlaylist(playlist);
  }, [playlist]);

  const [rolloverWrapper, setRolloverWrapper] = useState(false);

  useEffect(() => {
    setVideoCanPlay(false);
  }, [currentSessionIndex]);

  const IsMerchEnabled = () => {
    return (
      !wasBusinessCardShown &&
      player != null &&
      props.meta.enableMerchandiseScreen &&
      ((props.meta.merchandiseVideoUrl &&
        props.meta.merchandiseVideoUrl.length > 5) ||
        (props.meta.logoUrl && props.meta.logoUrl.length > 5))
    );
  };

  useEffect(() => {
    setIsPortal(true);
  }, []);

  const startNextPortalSession = () => {
    setCurrentPlaybackTime(0);
    setCurrentSessionIndex((prev) => {
      const len = visiblePlaylist.length;
      if (len === 0) return prev;

      let next = prev < len - 1 ? prev + 1 : 0;
      let attempts = 0;
      while (
        !visiblePlaylist[next]?.poster &&
        next !== prev &&
        attempts < len
      ) {
        next = next < len - 1 ? next + 1 : 0;
        attempts++;
      }

      return visiblePlaylist[next]?.poster ? next : prev;
    });
  };

  const startPreviousPortalSession = () => {
    setCurrentPlaybackTime(0);
    setCurrentSessionIndex((prev) => {
      if (prev <= 0) return prev;

      let next = prev - 1;
      let attempts = 0;
      while (!visiblePlaylist[next]?.poster && next > 0 && attempts < prev) {
        next--;
        attempts++;
      }

      return visiblePlaylist[next]?.poster ? next : prev;
    });
  };

  useEffect(() => {
    if (!playlist) {
      return;
    }

    TrackEvent(
      "Widget View",
      new Map([
        ["Title", props.meta.title],
        ["ID", props.pid],
        ["Author ID", props.meta.authorId],
        ["Author Name", props.meta.authorName],
      ]),
    );

    const fullScreen = (event: Event) => {
      // document.fullscreenElement will point to the element that
      // is in fullscreen mode if there is one. If there isn't one,
      // the value of the property is null.
      if (document.fullscreenElement) {
        console.log(
          `Element: ${document.fullscreenElement.id} entered full-screen mode.`,
        );
      }
    };

    document.addEventListener("fullscreenchange", fullScreen);

    return () => {
      document.removeEventListener("fullscreenchange", fullScreen);
    };
  }, []);

  const playerPlaylistSeek = (s: string) => {
    if (s == "prev" && currentIndex > 0) {
      startPreviousPortalSession();
    } else if (s == "next") {
      startNextPortalSession();
    }
  };

  const unMuteButton = (e: React.MouseEvent<Element, MouseEvent>) => {
    setIsAudioMuted(!isMuted);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      playerPlaylistSeek("next");
    },
    onSwipedRight: () => {
      playerPlaylistSeek("prev");
    },
    onSwipeStart: (dir) => {
      if (currentIndex == 0) {
        console.log("We've reached the start, we Swiped:" + dir);
      }
    },
    preventDefaultTouchmoveEvent: true,
  });

  // Main Render:

  if (playlist && playlist.length > 0) {
    return (
      <div className={cn("h-full cursor-pointer overflow-x-hidden relative")}>
        {process.env.NODE_ENV === "development" && isDevParam && false && (
          <Badge className="fixed z-9999 top-0 bg-yellow-500/50! text-yellow-300! right-0 m-2 mt-4 pointer-events-none flex flex-col gap-6">
            Dev Mode Portal
          </Badge>
        )}
        <div
          className={cn(
            "bg-cover",
            virginState
              ? "absolute inset-0 flex flex-col justify-center"
              : "hidden",
          )}
          onClick={() => setVirginState(false)}
        >
          <div
            className={cn(
              virginState
                ? "absolute inset-0 z-100 flex flex-col justify-center"
                : "hidden",
            )}
          >
            <div className="overflow-hidden delay-1000  duration-1000">
              <div
                className={cn(
                  "w-full h-full bg-cover mx-auto my-3 rounded-md shadow-lg aspect-video block",
                )}
                style={{
                  backgroundImage: `url(${
                    visiblePlaylist[currentSessionIndex ?? 0]?.poster ?? ""
                  })`,
                }}
              />
            </div>

            {virginState && <BigPlayButton portal={true} virginState={true} />}

            <div className="bottom-0 absolute bg-white dark:bg-zinc-800 w-full flex justify-center items-center">
              <div className="mt-10">
                <PortalFooter mini meta={props.meta} />
              </div>
            </div>
          </div>
        </div>
        <div
          className={cn("w-full h-full rounded-lg", virginState && "hidden")}
          {...swipeHandlers}
          onMouseLeave={() => {
            setRolloverWrapper(false);
          }}
          onMouseEnter={() => setRolloverWrapper(true)}
          onTouchEnd={() => setRolloverWrapper(false)}
        >
          {isMuted && true && (
            <div onClick={(e) => unMuteButton(e)}>
              <h3>
                <span>
                  <ImVolumeMute2 />
                </span>
                Tap to Unmute
              </h3>
            </div>
          )}
          <div className="w-full h-full rounded-lg" ref={playerWeb}>
            {hasContent && (
              <div
                className={cn(
                  isPlayerPaused || rolloverWrapper
                    ? null
                    : "*:pointer-events-auto opacity-0",
                  "absolute transition-opacity z-[100] *:flex flex *:gap-1 *:items-center inset-0 bg-gradient-to-b from-black via-black/80 to-transparent pointer-events-none",
                  " h-7 pl-2 pt-2",
                  "lg:h-12",
                  //   "bg-green-500",
                )}
              >
                <a href="/" className="px-1 " target="_blank">
                  <Icons.shellLogo className="h-6 -mr-2 lg:mr-0" />
                  <Icons.textLogo
                    className="hidden lg:block"
                    style={{ width: 55, color: "white", marginLeft: 5 }}
                  />
                </a>
                <a
                  className="w-full relative text-white flex  font-bold flex"
                  onClick={() => player?.pause()}
                  href={`/public/research/${props.pid}?source=portal`}
                  target="_blank"
                >
                  <p className="truncate absolute top-1/2 -translate-y-1/2 text-xs sm:text-sm md:text-base px-2 w-full ml-1">
                    {portalShowBusinessCard
                      ? "Support my Journalism"
                      : visiblePlaylist[currentIndex]?.title}
                  </p>
                </a>
              </div>
            )}
            {
              <>
                <div
                  id="PlayerAndHighlightFlexDiv"
                  className="h-full relative overflow-hidden"
                >
                  {hasContent && currentIndex > 0 && (
                    <div
                      className={cn(
                        isPlayerPaused
                          ? "-left-12"
                          : rolloverWrapper
                          ? "left-0"
                          : "-left-12",
                        "absolute z-10 transition-[left] h-24 w-24 top-1/2 flex items-center justify-start -translate-y-1/2",
                      )}
                    >
                      <div className="relative z-10 -left-2">
                        {" "}
                        <PlayPreviousButton
                          currentIndex={currentIndex}
                          onClick={startPreviousPortalSession}
                        />
                      </div>
                      <div className="rounded-full absolute -left-1/2 top-0 shadow-lg bg-background w-24 h-24" />
                    </div>
                  )}

                  {/* This is The Video Player */}
                  <div className="w-full relative h-full" ref={videoDiv}>
                    <AnimatePresence>
                      {hasContent ? (
                        <motion.div
                          className="h-full"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.5, delay: 0.2 }} // slight delay and duration
                        >
                          <VmPlayer portal={true} overlay={overlay} />
                        </motion.div>
                      ) : (
                        <motion.div
                          className="w-full flex place-items-center aspect-video fixed top-0 left-0 w-full h-full bg-[#1e262f] bg-background"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.5, delay: 0.2 }} // slight delay and duration
                        >
                          <Icons.spinner className="w-12 h-12 animate-spin text-stone-300" />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {hasContent && (
                      <div
                        className={cn(
                          "h-9 flex w-full absolute bottom-0 left-0 right-0",
                          isPipParam ? "h-6" : "h-9",
                        )}
                      >
                        <div className="relative h-full w-full flex bg-background">
                          <a
                            href={`/public/research/${props.pid}?source=portal`}
                            onClick={() => player?.pause()}
                            target="_blank"
                            rel="noop noreferrer"
                            className="holder z-20 *:flex  absolute top-0 h-full w-full bg-transparent"
                          />
                          <PortalFooter
                            meta={props.meta}
                            pip={isPipParam}
                            reelLength={
                              props.narrowed ? playlist.length : undefined
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className={cn(
                      rolloverWrapper
                        ? isPlayerPaused
                          ? "-right-12"
                          : "right-0"
                        : "-right-12",
                      "transition-[right] absolute h-24 top-1/2 flex items-center -translate-y-1/2",
                    )}
                  >
                    {
                      <div className="relative z-10 left-2">
                        {!isBusinessCardPlaying ? (
                          <PlayNextButton
                            playlist={visiblePlaylist}
                            currentIndex={currentIndex}
                            onClick={startNextPortalSession}
                          />
                        ) : (
                          <button
                            className={cn(
                              "text-background flex place-items-center w-12",
                            )}
                          >
                            <BsChevronRight
                              size={34}
                              onClick={startNextPortalSession}
                              className="arrows ChevronRight"
                            />
                          </button>
                        )}
                      </div>
                    }
                    <div className="rounded-full absolute top-0 shadow-lg bg-background w-24 h-24" />
                  </div>
                </div>
              </>
            }
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full h-full">
        <span
          className="mx-auto position-absolute"
          style={{ bottom: 0, right: "1.5em" }}
        >
          <Button asChild color="secondary">
            <a href="mailto:info@writeinstone.com">
              <span className="flex items-center gap-2">
                <MdEmail />
                Contact
              </span>
            </a>
          </Button>
        </span>
        <p color={COLORS.StoneClassyDarkColor} style={{ padding: "1em" }}>
          Project Not Found
        </p>
        <Image
          src="/images/SVG/noProject.svg"
          alt="No project found"
          width={300}
          height={300}
          unoptimized
          style={{ width: "300px", height: "auto" }}
        />
      </div>
    </>
  );
}
